import _ from "lodash";
import React, { useCallback, useState } from "react";
import { connect } from "react-redux";
import classNames from "classnames";

import { isIdEqual } from "Utils";
import { getDateTimeDifference, checkIsDateSame } from "Utils/DateTimeHelpers";
import {
  getUserId,
  getUserType,
  getCurrentCurriculumProgramType,
} from "modules/CommonSelectors";
import {
  CHAT_TYPE,
  CHAT_SCREEN_MODE,
  MESSAGE_TYPE,
  NORMAL_MESSAGE_ASSIGNMENT_ACTIVITY_TYPES,
  NORMAL_MESSAGE_ASSIGNMENT_ALERT_TYPES,
} from "Utils/MessageHelpers";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import Message from "UIComponents/Message";
import Loading from "UIComponents/Loading";
import TheatreModeV2 from "UIComponents/TheatreModeV2";

import classes from "./MessagesContainerV2.scss";

//this function will tell should given message is seperate or not
const getMessageSeperateState = ({
  message,
  messageToCompare,
  idx,
  type = "PREV",
}) => {
  const { createdBy, createdAt, parentMessage = {} } = message;
  const idxCond = _.isEqual(type, "PREV") ? idx > 0 : idx >= 0;

  const isSameSender =
    idxCond &&
    isIdEqual(_.get(createdBy, "id"), _.get(messageToCompare, "createdBy.id"));

  const isSameDate =
    idxCond &&
    checkIsDateSame({ date1: createdAt, date2: messageToCompare?.createdAt });

  const isConsecutiveMessageFromUser = isSameDate && isSameSender;

  const messageToCompareItemType = _.get(messageToCompare, "itemType");
  const isCompareMessageAssignmentActivity =
    _.includes(
      [
        MESSAGE_TYPE.ASSIGNMENT_ACTIVITY,
        MESSAGE_TYPE.STUDENT_ASSIGNMENT_ACTIVITY,
        MESSAGE_TYPE.ASSESSMENT_ACTIVITY,
      ],
      _.get(messageToCompare, "type")
    ) ||
    _.includes(
      NORMAL_MESSAGE_ASSIGNMENT_ACTIVITY_TYPES,
      messageToCompareItemType
    ) ||
    _.includes(NORMAL_MESSAGE_ASSIGNMENT_ALERT_TYPES, messageToCompareItemType);

  const timeDifferenceOfMessage =
    getDateTimeDifference({
      date1: createdAt,
      date2: messageToCompare?.createdAt,
      differenceBy: DATE_TIME_CONSTANTS.minutes,
    }) >= 2;

  const parentMessageToCompare = _.isEqual(type, "PREV")
    ? parentMessage
    : _.get(messageToCompare, "parentMessage", undefined);

  return (
    isCompareMessageAssignmentActivity ||
    !isConsecutiveMessageFromUser ||
    timeDifferenceOfMessage ||
    !_.isEmpty(parentMessageToCompare)
  );
};

const MessagesContainerV2 = props => {
  const {
    conversations: messages,
    conversationId,
    userId,
    userType,
    studentData,
    messagesConStyle,
    messagesScrollStyle,
    deleteMessageV2,
    editMessageV2,
    networkStatus,
    handleLoadPreviousMessages,
    setScrollContainerRef,
    curriculumProgramType,
    plannerElementSets,
    allowSendMessage = true,
    allowDeleteForAllMessages,
    messageContainerStyle,
    screenMode,
    renderLinksAsAnchor = true,
  } = props;

  const [mediaViewerData, setMediaViewerModalState] = useState(null);

  const openPreviewModal = useCallback(
    ({ attachments, selectedAttachment }) => {
      const activeAttachmentIndex = _.findIndex(attachments, attachment =>
        isIdEqual(attachment.id, selectedAttachment.id)
      );
      setMediaViewerModalState({
        attachments,
        activeAttachmentIndex,
      });
    },
    []
  );

  const closePreviewModal = useCallback(() => {
    setMediaViewerModalState(null);
  }, []);

  const renderMediaViewer = () => {
    if (_.isEmpty(mediaViewerData)) {
      return null;
    }

    const { attachments, activeAttachmentIndex } = mediaViewerData;

    return (
      <TheatreModeV2
        mediaAttachments={{ attachments }}
        activeAttachmentIndex={activeAttachmentIndex}
        toggleTheatreMode={closePreviewModal}
      />
    );
  };

  const bottomSpacerClasses = classNames({
    [classes.bottomMessageSpacer]: true,
    [classes.bottomMessageSpacerWithoutChatBar]: userType === "parent",
  });

  return (
    <div
      id={`chat-scroll-container-${conversationId}`}
      ref={setScrollContainerRef}
      className={classes.container}
      style={messagesConStyle}
      onScroll={handleLoadPreviousMessages}
      tabIndex={-1}
    >
      <div className={classes.innerScroll} style={messagesScrollStyle}>
        {networkStatus === 3 ? <Loading /> : null}
        <div className={classes.topMessageSpacer} />
        {_.map(messages, ({ node: message }, idx) => {
          const { id, type, createdAt, createdBy } = message;

          // undefined in the first message
          const previousMessage = _.get(messages, [idx - 1, "node"]);
          // undefined in the last message
          const nextMessage = _.get(messages, [idx + 1, "node"]);

          // For the first message in the list, always return false for both
          const isSameDateAsPreviousMessage =
            idx > 0 &&
            checkIsDateSame({
              date1: createdAt,
              date2: previousMessage?.createdAt,
            });

          const isSameSenderAsPreviousMessage =
            idx > 0 &&
            isIdEqual(
              _.get(createdBy, "id"),
              _.get(previousMessage, "createdBy.id")
            );

          const isNextConsecutiveMessage =
            !!nextMessage &&
            checkIsDateSame({
              date1: createdAt,
              date2: nextMessage.createdAt,
            }) &&
            isIdEqual(
              _.get(createdBy, "id"),
              _.get(nextMessage, "createdBy.id")
            );

          const isCurrentMessageSeperated = getMessageSeperateState({
            message,
            messageToCompare: previousMessage,
            type: "PREV",
            idx,
          });

          const isNextMessageSeperated = getMessageSeperateState({
            message,
            messageToCompare: nextMessage,
            type: "NEXT",
            idx,
          });

          const messageScreenMode = allowSendMessage
            ? CHAT_SCREEN_MODE.FULL_SCREEN
            : CHAT_SCREEN_MODE.VIEW_MODE_SCREEN;

          return (
            <Message
              key={`${type}-${id}`}
              conversationId={conversationId}
              message={message}
              chatType={CHAT_TYPE.CHAT}
              screenMode={screenMode ? screenMode : messageScreenMode}
              activeUserId={userId}
              openPreviewModal={openPreviewModal}
              isNextConsecutiveMessage={isNextConsecutiveMessage}
              isCurrentMessageSeperated={isCurrentMessageSeperated}
              isNextMessageSeperated={isNextMessageSeperated}
              isSameDateAsPreviousMessage={isSameDateAsPreviousMessage}
              isSameSenderAsPreviousMessage={isSameSenderAsPreviousMessage}
              isMessageOptionsVisible={true}
              onEditMessageClick={editMessageV2}
              deleteMessage={deleteMessageV2}
              studentData={studentData}
              currentUserType={userType}
              plannerElementSets={plannerElementSets}
              curriculumProgramType={curriculumProgramType}
              allowDeleteForAllMessages={allowDeleteForAllMessages}
              messageContainerStyle={messageContainerStyle}
              renderLinksAsAnchor={renderLinksAsAnchor}
            />
          );
        })}
        <div className={bottomSpacerClasses} />
      </div>
      {renderMediaViewer()}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const userId = getUserId(state);
  const userType = getUserType(state);
  const plannerElementSets = _.get(
    state,
    "platform.currentPlannerElementSets",
    []
  );
  const curriculumProgramType = getCurrentCurriculumProgramType(state);
  return {
    userId,
    userType,
    plannerElementSets,
    curriculumProgramType,
  };
};

export default connect(mapStateToProps, null)(MessagesContainerV2);
